
import { useEffect, useRef, useState } from 'react';
import NeuralNetworkBg from '../../../components/feature/NeuralNetworkBg';

const points = [
  { text: 'Built for behavioral change', icon: 'ri-arrow-right-up-line' },
  { text: 'Structured not passive', icon: 'ri-layout-grid-line' },
  { text: 'Brain body nervous system integration', icon: 'ri-links-line' },
  { text: 'Designed for real-time use', icon: 'ri-timer-flash-line' },
  { text: 'Creates lasting baseline change', icon: 'ri-line-chart-line' },
];

export default function Differentiation() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!('IntersectionObserver' in window)) {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry && entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    const current = sectionRef.current;
    if (current) observer.observe(current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative py-28 lg:py-36 px-6 lg:px-12 overflow-hidden"
    >
      {/* Soft gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-rose-50/20 to-violet-50/15"></div>

      {/* Neural pathways */}
      <NeuralNetworkBg
        color="212,120,158"
        nodeCount={20}
        opacity={0.06}
        speed={0.35}
        variant="light"
      />

      <NeuralNetworkBg
        color="77,184,199"
        nodeCount={12}
        opacity={0.04}
        speed={0.25}
        variant="light"
      />

      {/* Soft floating orbs */}
      <div className="absolute inset-0 overflow-hidden">
        <div
          className="absolute w-[500px] h-[500px] rounded-full animate-float-slow opacity-[0.06]"
          style={{
            background:
              'radial-gradient(circle, rgba(212,120,158,0.5) 0%, transparent 70%)',
            top: '10%',
            right: '-10%',
          }}
        ></div>
        <div
          className="absolute w-[400px] h-[400px] rounded-full animate-float opacity-[0.05]"
          style={{
            background:
              'radial-gradient(circle, rgba(154,123,212,0.4) 0%, transparent 70%)',
            bottom: '5%',
            left: '-5%',
            animationDelay: '2s',
          }}
        ></div>
      </div>

      <div className="relative z-10 max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left - Image */}
          <div
            className={`transition-all duration-1000 ${
              visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
            }`}
          >
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-brand-orange/12 via-brand-pink/12 to-brand-violet/12 rounded-3xl blur-2xl animate-pulse-glow"></div>
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://public.readdy.ai/ai/img_res/63b145262db757385eb8263d0eb67f7d.jpg"
                  alt="Person experiencing calm focus"
                  className="w-full h-[500px] object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent"></div>
              </div>
            </div>
          </div>

          {/* Right - Content */}
          <div
            className={`transition-all duration-1000 delay-200 ${
              visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
            }`}
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-brand-pink/5 border border-brand-pink/8 mb-8">
              <div className="w-2 h-2 rounded-full bg-brand-pink animate-pulse"></div>
              <span className="text-xs font-semibold text-brand-pink uppercase tracking-widest">
                Different
              </span>
            </div>

            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 tracking-tight leading-tight">
              Most tools{' '}
              <span className="gradient-text">soothe</span> you.
              <br />
              This one{' '}
              <span className="gradient-text">retrains</span> you.
            </h2>

            <p className="text-base text-gray-400 mb-10 leading-relaxed">
              Brain Wash isn&apos;t another meditation app. It&apos;s a
              structured reconditioning system that creates real, measurable
              change in how your brain operates.
            </p>

            <div className="space-y-4">
              {points.map((point, index) => (
                <div
                  key={index}
                  className={`group flex items-center gap-4 p-4 rounded-xl bg-white/80 backdrop-blur-sm border border-gray-100/80 hover:border-brand-pink/15 hover:shadow-lg hover:shadow-brand-pink/5 transition-all duration-500 cursor-pointer ${
                    visible ? 'animate-fade-in-up' : 'opacity-0'
                  }`}
                  style={{ animationDelay: `${0.3 + index * 0.1}s` }}
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-gradient-to-br from-brand-orange/8 via-brand-pink/8 to-brand-violet/8 group-hover:scale-110 transition-transform duration-300">
                    <i
                      className={`${point.icon} text-lg text-brand-pink`}
                      aria-hidden="true"
                    ></i>
                  </div>
                  <p className="text-base font-medium text-gray-700 tracking-tight">
                    {point.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
